import { useCallback, useEffect } from "react";

export type MenuFocusTarget = "first" | "last";

export type ComposerMenuController = {
  openMenu: (target?: MenuFocusTarget) => void;
  closeMenu: (options?: { restoreFocus?: boolean }) => void;
  focusMenuItem: (menu: HTMLElement | null, target?: MenuFocusTarget, selector?: string) => void;
  moveMenuFocus: (menu: HTMLElement | null, direction: -1 | 1, selector?: string) => void;
  onTriggerKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  onMenuKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
};

function focusableButtons(menu: HTMLElement | null, selector = "button:not([disabled])") {
  if (!menu) return [];
  return Array.from(menu.querySelectorAll<HTMLButtonElement>(selector))
    .filter((button) => button.offsetParent !== null);
}

export function useComposerMenu({
  open,
  setOpen,
  triggerRef,
  menuRef
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  menuRef: React.RefObject<HTMLElement | null>;
}): ComposerMenuController {
  const focusMenuItem = useCallback((menu: HTMLElement | null, target: MenuFocusTarget = "first", selector?: string) => {
    const items = focusableButtons(menu, selector);
    const item = target === "last" ? items.at(-1) : items[0];
    item?.focus();
  }, []);

  const moveMenuFocus = useCallback((menu: HTMLElement | null, direction: -1 | 1, selector?: string) => {
    const items = focusableButtons(menu, selector);
    if (!items.length) return;
    const current = document.activeElement instanceof HTMLButtonElement ? document.activeElement : null;
    const currentIndex = current ? items.indexOf(current) : -1;
    const nextIndex = currentIndex < 0
      ? (direction === 1 ? 0 : items.length - 1)
      : (currentIndex + direction + items.length) % items.length;
    items[nextIndex]?.focus();
  }, []);

  const closeMenu = useCallback((options: { restoreFocus?: boolean } = {}) => {
    setOpen(false);
    if (options.restoreFocus === false) return;
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, [setOpen, triggerRef]);

  const openMenu = useCallback((target: MenuFocusTarget = "first") => {
    setOpen(true);
    requestAnimationFrame(() => focusMenuItem(menuRef.current, target));
  }, [focusMenuItem, menuRef, setOpen]);

  useEffect(() => {
    if (!open) return;
    const dismissOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      closeMenu({ restoreFocus: false });
    };
    document.addEventListener("pointerdown", dismissOnOutsidePointer);
    return () => document.removeEventListener("pointerdown", dismissOnOutsidePointer);
  }, [closeMenu, menuRef, open, triggerRef]);

  const onTriggerKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      closeMenu();
      return;
    }
    if (event.key !== "Enter" && event.key !== " " && event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    if (open) {
      focusMenuItem(menuRef.current, event.key === "ArrowUp" ? "last" : "first");
    } else {
      openMenu(event.key === "ArrowUp" ? "last" : "first");
    }
  }, [closeMenu, focusMenuItem, menuRef, open, openMenu]);

  const onMenuKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveMenuFocus(event.currentTarget, event.key === "ArrowDown" ? 1 : -1);
    }
  }, [closeMenu, moveMenuFocus]);

  return { openMenu, closeMenu, focusMenuItem, moveMenuFocus, onTriggerKeyDown, onMenuKeyDown };
}
